///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Multi-stop story playback: fly leg → arrive → dwell (hold or orbit) →
/// next leg, sequencing the same one-leg TourPlayer that saved-view
/// fly-to uses. Two modes: reader-paced (viewer advances manually) and
/// guided (auto-advance after each stop's dwell time).
///
/// Owned and ticked by CameraDirector. User input pauses the story
/// (cameraMode "tourPaused", card offers "Resume story"); resume flies
/// back to the current stop. Per-frame progress never touches redux -
/// only stop transitions and mode changes are dispatched (activeStory on
/// dialogSlice, like cameraMode).
///

import { CameraParams } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import type { EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import type { Story, StoryStop } from "@/redux/storiesSlice"
import { clearTargetFromHistory, parseStoryTarget, pushTargetToHistory } from "@/utils/navigationHistory"
import { mountStoryDatasets } from "@/utils/storyDatasets"
import { enableSceneLayers, focusSceneTarget } from "./sceneState"
import type { OrbitBehavior } from "./OrbitBehavior"
import type { TourPlayer } from "./TourPlayer"

/** Guided-mode dwell when a stop doesn't specify one (seconds) */
const cDefaultGuidedDwellSeconds = 6

/**
 * How long to wait for a focus transition to begin after dispatching a
 * stop's focus target. If the entity never starts one (missing, layer
 * still loading), fall back to flying the stop's embedded pose.
 */
const cFocusStartTimeoutSeconds = 1.5

/** Orbit angular velocity fallback; matches cDefaultTourSettings.orbitSpeed */
const cDefaultOrbitSpeed = 0.02

/**
 * A story can live in the user's stories or arrive via a share link
 * (dialog.sharedStoryPreview) - recipients play without importing.
 */
function lookupStory(storyId: string): Story | undefined {
	const state = engineBridge.getState()
	if (!state) return undefined
	const preview = state.dialog.sharedStoryPreview
	return state.stories.stories[storyId]
		?? (preview?.id === storyId ? preview : undefined)
}

type Phase =
	| "idle"        // no story
	| "focusWait"   // stop has a focus target; waiting for its transition
	| "legWait"     // waiting for a focus release to finish before flying
	| "leg"         // TourPlayer flying to the stop's pose
	| "dwell"       // arrived; holding or orbiting while the card reads
	| "paused"      // user took the camera; story position retained

export class StoryPlayer {
	private context: EarthBrowserContext
	private tourPlayer: TourPlayer
	private orbitBehavior: OrbitBehavior

	private storyId: string | null = null
	private stopIndex = 0
	private guided = false
	private phase: Phase = "idle"

	private focusTransitionSeen = false
	private focusWaitElapsed = 0
	private dwellElapsed = 0

	constructor(context: EarthBrowserContext, tourPlayer: TourPlayer, orbitBehavior: OrbitBehavior) {
		this.context = context
		this.tourPlayer = tourPlayer
		this.orbitBehavior = orbitBehavior
	}

	/** A story exists (playing or paused); the card overlay is up */
	public get hasStory(): boolean {
		return this.storyId !== null
	}

	/** Actively driving the camera (CameraDirector delegates its tick) */
	public get engaged(): boolean {
		return this.storyId !== null && this.phase !== "paused" && this.phase !== "idle"
	}

	private get story(): Story | undefined {
		return this.storyId ? lookupStory(this.storyId) : undefined
	}

	private get currentStop(): StoryStop | undefined {
		return this.story?.stops[this.stopIndex]
	}

	/** Start playback at a stop (deep links start mid-story) */
	public play(storyId: string, stopIndex = 0, guided = false): void {
		const story = lookupStory(storyId)
		if (!story || story.stops.length === 0) return
		this.storyId = storyId
		this.guided = guided
		// Make the running story shareable/navigable (story-<uuid> target)
		pushTargetToHistory("story", storyId)
		this.goToStop(Math.max(0, Math.min(story.stops.length - 1, stopIndex)))
	}

	/** End playback and clear the card */
	public stopPlayback(): void {
		if (!this.storyId) return
		this.stopMotion()
		this.storyId = null
		this.phase = "idle"
		engineBridge.dispatch({ type: engineActions.setActiveStory, payload: null })
		engineBridge.dispatch({ type: engineActions.setCameraMode, payload: "free" })

		// Drop the story target from the URL (unless a stop's entity focus
		// has already replaced it with its own target)
		const target = new URL(window.location.href).searchParams.get("target")
		if (target && parseStoryTarget(target)) {
			clearTargetFromHistory()
		}
	}

	/** User input took the camera; keep the story position for resume */
	public pause(): void {
		if (!this.storyId || this.phase === "paused") return
		this.stopMotion()
		this.phase = "paused"
		engineBridge.dispatch({ type: engineActions.setCameraMode, payload: "tourPaused" })
	}

	/** Fly back to the current stop after a pause */
	public resume(): void {
		if (!this.storyId) return
		this.goToStop(this.stopIndex)
	}

	public next(): void {
		if (!this.storyId) return
		this.goToStop(this.stopIndex + 1)
	}

	public prev(): void {
		if (!this.storyId) return
		this.goToStop(this.stopIndex - 1)
	}

	/**
	 * Begin the leg to a stop: publish the new position, apply the stop's
	 * scene (layers enable-only, focus target), then either ride the focus
	 * transition or fly the embedded pose once any focus release finishes.
	 */
	private goToStop(index: number): void {
		const storyId = this.storyId
		const story = this.story
		if (!storyId || !story || story.stops.length === 0) {
			this.stopPlayback()
			return
		}
		if (index < 0 || index >= story.stops.length) return

		this.stopMotion()
		this.stopIndex = index
		this.dwellElapsed = 0

		engineBridge.dispatch({
			type: engineActions.setActiveStory,
			payload: { storyId, stopIndex: index, guided: this.guided },
		})
		engineBridge.dispatch({ type: engineActions.setCameraMode, payload: "tour" })

		const stop = story.stops[index]
		enableSceneLayers(stop.layers)
		// Fetch + mount referenced cloud datasets (async, fail-soft: the
		// stop plays even when a dataset is gone or unreachable)
		mountStoryDatasets(story, stop)

		// A focus target owns the camera: the focus transition frames the
		// entity itself, so no pose leg flies (same as saved-view fly-to)
		if (stop.focusTargetId && focusSceneTarget(stop.focusTargetId)) {
			this.focusTransitionSeen = false
			this.focusWaitElapsed = 0
			this.phase = "focusWait"
			return
		}

		// Return focus to Earth first; the leg starts once the release
		// transition completes (it controls the camera until then)
		const focusManager = this.context.focusManager
		const focused = focusManager.getFocusedNode()
		if (focused && focused.id !== "earth") {
			focusManager.releaseFocus({ preserveCurrentState: true })
		}
		this.phase = "legWait"
	}

	/** Per-frame tick from CameraDirector; dt in seconds */
	public update(dt: number): void {
		if (!this.engaged) return

		const stop = this.currentStop
		if (!stop) {
			// Story deleted or stops removed while playing
			this.stopPlayback()
			return
		}

		switch (this.phase) {
			case "focusWait": {
				const transitioning = this.context.focusManager.isTransitioning()
				if (transitioning) this.focusTransitionSeen = true
				if (this.focusTransitionSeen && !transitioning) {
					this.beginDwell(stop)
				} else if (!this.focusTransitionSeen) {
					this.focusWaitElapsed += dt
					if (this.focusWaitElapsed >= cFocusStartTimeoutSeconds) {
						// Entity never started a transition (missing or its
						// layer is still loading): fly the embedded pose
						this.startLeg(stop)
					}
				}
				break
			}

			case "legWait": {
				if (!this.context.focusManager.isTransitioning()) {
					this.startLeg(stop)
				}
				break
			}

			case "leg": {
				this.tourPlayer.update(dt)
				if (!this.tourPlayer.playing) {
					this.beginDwell(stop)
				}
				break
			}

			case "dwell": {
				if (stop.dwell.mode === "orbit") {
					const speed = engineBridge.getState()?.earthbrowser.tourSettings.orbitSpeed ?? cDefaultOrbitSpeed
					this.orbitBehavior.update(dt, speed)
				}

				// Guided autoplay advances after the dwell; the last stop
				// dwells indefinitely (the viewer closes the card)
				const story = this.story
				if (this.guided && story && this.stopIndex < story.stops.length - 1) {
					this.dwellElapsed += dt
					const dwellSeconds = stop.dwell.seconds ?? cDefaultGuidedDwellSeconds
					if (this.dwellElapsed >= dwellSeconds) {
						this.goToStop(this.stopIndex + 1)
					}
				}
				break
			}
		}
	}

	private startLeg(stop: StoryStop): void {
		const c = stop.camera
		this.tourPlayer.startLeg(new CameraParams(c.radius, c.theta, c.phi, c.tilt, c.bearing))
		this.phase = "leg"
	}

	private beginDwell(stop: StoryStop): void {
		this.dwellElapsed = 0
		if (stop.dwell.mode === "orbit") {
			this.orbitBehavior.start()
		}
		this.phase = "dwell"
	}

	private stopMotion(): void {
		this.tourPlayer.stop()
		this.orbitBehavior.stop()
	}
}
